import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, MapPin, Plus, MessageCircle, ShoppingBag, Loader2, Sparkles, ChevronRight, Coffee } from "lucide-react";
import { waUrl } from "@/lib/brand";

export const WaysToEnjoy = ({ data }) => (
  <section className="py-14 sm:py-20 lg:py-24 bg-[#FFF8E7]" data-testid="landing-ways">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5">
        <div className="chip chip-gold mb-4"><Coffee className="w-3.5 h-3.5" /> {data.kicker}</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18] leading-[1.08]">
          {data.heading[0]} <span className="block italic font-serif-alt text-[#2E7D32]">{data.heading[1]}</span>
        </h2>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="mt-8 rounded-[28px] overflow-hidden aspect-[4/3] shadow-[0_30px_60px_-30px_rgba(20,60,24,0.45)]">
          <img src={data.image} alt="Rudrima pure forest honey 500g jar" width="800" height="600" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </motion.div>
      </div>
      <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {data.items.map((it, i) => (
          <motion.li
            key={it.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: i * 0.07 }}
            className={`p-6 rounded-3xl bg-white border border-[#8D6E63]/12 hover:border-[#D4A017]/50 transition-colors ${i === data.items.length - 1 ? "sm:col-span-2" : ""}`}
          >
            <div className="font-display text-2xl text-[#D4A017] leading-none mb-3">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="font-display text-xl font-bold text-[#143C18]">{it.title}</h3>
            <p className="mt-1.5 text-[#5D4037] text-[15px]">{it.desc}</p>
          </motion.li>
        ))}
      </ul>
    </div>
  </section>
);

const ReviewCard = ({ r, color }) => (
  <div className="card-premium p-7 h-full flex flex-col min-w-[85%] sm:min-w-0" style={{ scrollSnapAlign: "start" }}>
    <div className="flex gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
      {[...Array(r.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#D4A017] text-[#D4A017]" />)}
    </div>
    <p className="text-[#1A1614] leading-relaxed flex-1 italic font-serif-alt text-lg">&ldquo;{r.text}&rdquo;</p>
    <div className="mt-5 pt-5 border-t border-[#8D6E63]/15 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: color }}>{r.initial}</div>
      <div>
        <div className="font-bold text-[#143C18]">{r.name}</div>
        <div className="text-xs text-[#8D6E63] flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city}</div>
      </div>
    </div>
  </div>
);

export const LandingReviews = ({ reviews, colors }) => (
  <section id="reviews" className="py-14 sm:py-20 lg:py-24 bg-[#FBF4E1]" data-testid="landing-reviews">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <div className="chip mb-4"><Star className="w-3.5 h-3.5 fill-current" /> Customer Reviews</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18]">What families <span className="italic font-serif-alt text-[#D4A017]">say</span></h2>
        </div>
        <Link to="/#reviews" className="text-sm font-semibold text-[#2E7D32] inline-flex items-center gap-1 hover:gap-2 transition-all">All reviews <ChevronRight className="w-4 h-4" /></Link>
      </div>
      <div className="flex sm:grid sm:grid-cols-3 gap-4 sm:gap-6 overflow-x-auto sm:overflow-visible no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 pb-2" style={{ scrollSnapType: "x mandatory" }}>
        {reviews.map((r, i) => <ReviewCard key={r.name} r={r} color={colors[i % colors.length]} />)}
      </div>
    </div>
  </section>
);

export const LandingFAQ = ({ faqs, productName }) => (
  <section id="faq" className="py-14 sm:py-20 lg:py-24 bg-[#FFF8E7]" data-testid="landing-faq">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
      <div className="text-center mb-12">
        <div className="chip mb-3"><MessageCircle className="w-3.5 h-3.5" /> FAQ</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18]">{productName} <span className="italic font-serif-alt text-[#D4A017]">questions</span></h2>
      </div>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <motion.details key={f.q} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.04 }} className="group rounded-2xl bg-white border border-[#8D6E63]/15 open:border-[#2E7D32]/40 open:shadow-lg transition-all overflow-hidden" data-testid={`faq-item-${i}`}>
            <summary className="flex items-center justify-between gap-4 p-5 sm:p-6 cursor-pointer">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-[#143C18]">{f.q}</h3>
              <Plus className="acc-icon w-5 h-5 text-[#2E7D32] transition-transform shrink-0" />
            </summary>
            <div className="px-5 sm:px-6 pb-6 text-[#5D4037] leading-relaxed">{f.a}</div>
          </motion.details>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-[#8D6E63]">
        Still have a question? <Link to="/contact-us" className="text-[#2E7D32] font-semibold underline-offset-2 hover:underline">Contact us</Link> or message us on WhatsApp.
      </p>
    </div>
  </section>
);

export const FinalCTA = ({ data, shop }) => (
  <section className="py-14 sm:py-20 lg:py-28 relative overflow-hidden bg-forest-deep" data-testid="landing-final-cta">
    <div className="absolute inset-0 opacity-20 pointer-events-none grain" />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 relative text-center">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
        <div className="chip chip-gold mb-5"><Sparkles className="w-3.5 h-3.5" /> Small batches · Secure checkout</div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">{data.heading}</h2>
        <p className="mt-5 text-white/85 text-lg max-w-2xl mx-auto">{data.sub}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={() => shop.buyNow(1)} disabled={shop.buying || !shop.available} data-testid="final-cta-buy-btn" className="btn-gold !py-3.5 disabled:opacity-60">
            {shop.buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} {data.label} – {shop.priceLabel}
          </button>
          <a href={waUrl()} target="_blank" rel="noopener noreferrer" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#143C18] !py-3.5">
            <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

export const StickyBuyBar = ({ config, shop }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[#FFF8E7]/95 backdrop-blur-xl border-t border-[#8D6E63]/15 px-3 pt-2.5 shadow-[0_-10px_30px_-15px_rgba(20,60,24,0.35)]"
          style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
          data-testid="sticky-buy-bar"
        >
          <div className="flex items-center gap-3">
            <div className="leading-tight pl-1">
              <div className="text-[10px] uppercase tracking-widest text-[#8D6E63] font-bold">{config.shortName} · {config.weight}</div>
              <div className="font-display text-xl font-bold text-[#143C18]" data-testid="sticky-buy-price">{shop.priceLabel}</div>
            </div>
            <button onClick={() => shop.buyNow(1)} disabled={shop.buying || !shop.available || shop.loading} data-testid="sticky-buy-now-btn" className="btn-primary flex-1 justify-center !py-3 text-[15px] disabled:opacity-60">
              {shop.buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Buy Now
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
